// src/pages/clab-biodata-amended/components/FilterPanel.jsx
import React, { useState, useEffect } from "react";
import Icon from "components/AppIcon";
import { recruiterAPI } from "utils/api";

const FilterPanel = ({ onFilterChange }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [filters, setFilters] = useState({
    search: "",
    nationality: "",
    recruiter_id: "",
    date_from: "",
    date_to: "",
    skill: "",
  });
  const [recruiters, setRecruiters] = useState([]);
  const [localSearch, setLocalSearch] = useState("");
  const [localSkill, setLocalSkill] = useState("");

  // Debounce search term - wait 500ms after user stops typing
  useEffect(() => {
    // Only trigger if value actually changed
    if (localSearch === filters.search) return;

    const handler = setTimeout(() => {
      const newFilters = { ...filters, search: localSearch };
      setFilters(newFilters);
      onFilterChange(newFilters);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [localSearch]);

  // Debounce skill term - route to search
  useEffect(() => {
    if (localSkill === filters.search) return;
    const handler = setTimeout(() => {
      const newFilters = { ...filters, search: localSkill };
      setFilters(newFilters);
      onFilterChange(newFilters);
    }, 500);
    return () => {
      clearTimeout(handler);
    };
  }, [localSkill]);

  useEffect(() => {
    let isMounted = true;

    const loadRecruiters = async () => {
      try {
        const result = await recruiterAPI.getAllRecruiters();
        if (result.status === "success" && isMounted) {
          setRecruiters(result.data.data || []);
        }
      } catch (error) {
      }
    };

    loadRecruiters();

    return () => {
      isMounted = false;
    };
  }, []);

  const nationalities = [
    "Bangladesh",
    "India",
    "Philippines",
    "China",
    "Pakistan",
    "Sri Lanka",
    "Nepal",
    "Myanmar",
    "Vietnam",
    "Indonesia",
  ];

  const handleFilterChange = (key, value) => {
    const newFilters = {
      ...filters,
      [key]: value,
    };
    setFilters(newFilters);
    onFilterChange(newFilters);
  };

  const clearAllFilters = () => {
    setLocalSearch("");
    setLocalSkill("");
    const clearedFilters = {
      search: "",
      nationality: "",
      recruiter_id: "",
      date_from: "",
      date_to: "",
      skill: "",
    };
    setFilters(clearedFilters);
    onFilterChange(clearedFilters);
  };

  const hasActiveFilters =
    filters.search ||
    filters.nationality ||
    filters.recruiter_id ||
    filters.date_from ||
    filters.date_to ||
    filters.skill;

  return (
    <div className="bg-surface rounded-lg border border-border">
      <div className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-text-primary">
            Search & Filter
          </h2>
          <div className="flex items-center space-x-3">
            {hasActiveFilters && (
              <button
                onClick={clearAllFilters}
                className="text-sm text-danger hover:text-danger-dark font-medium"
              >
                Clear All
              </button>
            )}
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="flex items-center space-x-1 text-sm text-primary hover:text-primary-dark font-medium"
            >
              <span>{isExpanded ? "Less Filters" : "More Filters"}</span>
              <Icon
                name={isExpanded ? "chevron-up" : "chevron-down"}
                size={16}
              />
            </button>
          </div>
        </div>

        {/* Search Bar */}
        <div className="mb-4">
          <div className="relative">
            <Icon
              name="search"
              size={20}
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-tertiary"
            />
            <input
              type="text"
              placeholder="Search by worker name, passport... (Press Enter or wait 0.5s)"
              value={localSearch}
              onChange={(e) => setLocalSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  const newFilters = { ...filters, search: localSearch };
                  setFilters(newFilters);
                  onFilterChange(newFilters);
                }
              }}
              className="w-full pl-10 pr-4 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
            />
          </div>
        </div>

        {/* Basic Filters */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-text-secondary mb-2">
              Nationality
            </label>
            <select
              value={filters.nationality}
              onChange={(e) =>
                handleFilterChange("nationality", e.target.value)
              }
              className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
            >
              <option value="">All Nationalities</option>
              {nationalities.map((nationality) => (
                <option key={nationality} value={nationality}>
                  {nationality}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-text-secondary mb-2">
              Recruiter
            </label>
            <select
              value={filters.recruiter_id}
              onChange={(e) =>
                handleFilterChange("recruiter_id", e.target.value)
              }
              className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
            >
              <option value="">All Recruiters</option>
              {recruiters.map((recruiter) => (
                <option key={recruiter.id} value={recruiter.id}>
                  {recruiter.company_name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-text-secondary mb-2">
              Skill keyword
            </label>
            <input
              type="text"
              value={localSkill}
              onChange={(e) => setLocalSkill(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  const newFilters = { ...filters, search: localSkill };
                  setFilters(newFilters);
                  onFilterChange(newFilters);
                }
              }}
              className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
              placeholder="e.g. welding, scaffolding (Press Enter or wait 0.5s)"
            />
          </div>
        </div>

        {/* Advanced Filters */}
        {isExpanded && (
          <div className="border-t border-border pt-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-text-secondary mb-2">
                  Amendment Date From
                </label>
                <input
                  type="date"
                  value={filters.date_from}
                  onChange={(e) =>
                    handleFilterChange("date_from", e.target.value)
                  }
                  className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-text-secondary mb-2">
                  Amendment Date To
                </label>
                <input
                  type="date"
                  value={filters.date_to}
                  onChange={(e) =>
                    handleFilterChange("date_to", e.target.value)
                  }
                  className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                />
              </div>
            </div>

            {/* Quick Filter Buttons */}
            <div className="mt-4 pt-4 border-t border-border">
              <div className="flex items-center space-x-2 mb-2">
                <span className="text-sm font-medium text-text-secondary">
                  Quick Filters:
                </span>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => {
                    const today = new Date().toISOString().split("T")[0];
                    handleFilterChange("date_from", today);
                    handleFilterChange("date_to", today);
                  }}
                  className="px-3 py-1 text-sm rounded-full border bg-surface text-text-secondary border-border hover:bg-background transition-colors duration-150"
                >
                  Today's Amendments
                </button>
                <button
                  onClick={() => {
                    const weekAgo = new Date();
                    weekAgo.setDate(weekAgo.getDate() - 7);
                    handleFilterChange(
                      "date_from",
                      weekAgo.toISOString().split("T")[0]
                    );
                    handleFilterChange("date_to", "");
                  }}
                  className="px-3 py-1 text-sm rounded-full border bg-surface text-text-secondary border-border hover:bg-background transition-colors duration-150"
                >
                  Last 7 Days
                </button>
                <button
                  onClick={() => {
                    const monthAgo = new Date();
                    monthAgo.setMonth(monthAgo.getMonth() - 1);
                    handleFilterChange(
                      "date_from",
                      monthAgo.toISOString().split("T")[0]
                    );
                    handleFilterChange("date_to", "");
                  }}
                  className="px-3 py-1 text-sm rounded-full border bg-surface text-text-secondary border-border hover:bg-background transition-colors duration-150"
                >
                  Last Month
                </button>
                <button
                  onClick={() => {
                    handleFilterChange("nationality", "Bangladesh");
                  }}
                  className="px-3 py-1 text-sm rounded-full border bg-surface text-text-secondary border-border hover:bg-background transition-colors duration-150"
                >
                  Bangladesh Workers
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Active Filters Display */}
        {hasActiveFilters && (
          <div className="mt-4 pt-4 border-t border-border">
            <div className="flex items-center space-x-2 mb-2">
              <span className="text-sm font-medium text-text-secondary">
                Active Filters:
              </span>
            </div>
            <div className="flex flex-wrap gap-2">
              {filters.search && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
                  Search: {filters.search}
                  <button
                    onClick={() => handleFilterChange("search", "")}
                    className="ml-2 hover:text-primary-900"
                  >
                    <Icon name="x" size={12} />
                  </button>
                </span>
              )}
              {filters.nationality && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-info-100 text-info-800">
                  Nationality: {filters.nationality}
                  <button
                    onClick={() => handleFilterChange("nationality", "")}
                    className="ml-2 hover:text-info-900"
                  >
                    <Icon name="x" size={12} />
                  </button>
                </span>
              )}
              {filters.recruiter_id && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-warning-100 text-warning-800">
                  Recruiter:{" "}
                  {recruiters.find((r) => r.id == filters.recruiter_id)
                    ?.company_name || "Selected"}
                  <button
                    onClick={() => handleFilterChange("recruiter_id", "")}
                    className="ml-2 hover:text-warning-900"
                  >
                    <Icon name="x" size={12} />
                  </button>
                </span>
              )}
              {(filters.date_from || filters.date_to) && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-success-100 text-success-800">
                  Date: {filters.date_from || "Any"} to{" "}
                  {filters.date_to || "Any"}
                  <button
                    onClick={() => {
                      handleFilterChange("date_from", "");
                      handleFilterChange("date_to", "");
                    }}
                    className="ml-2 hover:text-success-900"
                  >
                    <Icon name="x" size={12} />
                  </button>
                </span>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FilterPanel;
